import { Reveal } from "./motion-primitives";
import { SectionLabel } from "./About";
import { CountUp } from "./enhancements";
import { useLang, Rich } from "@/i18n/LanguageContext";

const METRIC_VALUES = [
  { to: 20, prefix: "> ", suffix: "" },
  { to: 5, prefix: "> ", suffix: "" },
  { to: 120, prefix: "< ", suffix: " m" },
  { to: 5000, prefix: "> ", suffix: "" },
];

const Step = ({ n, t, d }) => (
  <Reveal className="relative pl-12 md:pl-16">
    <span className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-full border border-paper/25 font-sans text-[11px] text-mist md:h-9 md:w-9">
      {n}
    </span>
    <h4 className="mb-2 font-serif text-xl md:text-2xl">{t}</h4>
    <p className="font-sans text-[13px] leading-[1.7] text-paper/55">{d}</p>
  </Reveal>
);

export const DevelopmentModel = () => {
  const { c } = useLang();
  const m = c.model;
  return (
    <section id="model" data-testid="section-model" className="relative overflow-hidden bg-deep px-6 py-24 text-paper md:px-[8vw] md:py-32">
      <div className="pointer-events-none absolute left-0 top-1/4 h-[520px] w-[45%]" style={{ background: "radial-gradient(circle at 30% 40%, rgba(80,180,160,.12), transparent 55%)" }} />
      <div className="relative">
        <Reveal>
          <SectionLabel n="04" onDark>{m.label}</SectionLabel>
        </Reveal>

        <div className="grid gap-10 md:grid-cols-[1.05fr_0.95fr] md:gap-[11vw]">
          <Reveal>
            <h2 className="font-serif text-[clamp(44px,5.2vw,80px)] font-normal leading-[0.98] tracking-[-0.02em]">
              <Rich text={m.title} />
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="max-w-[560px] pt-2">
            <p className="font-sans text-[15px] leading-[1.8] text-paper/60">{m.intro}</p>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-16 md:grid-cols-2 md:gap-[8vw]">
          <div>
            <p className="mb-10 font-sans text-[11px] uppercase tracking-[0.2em] text-teal">{m.phase1Label}</p>
            <div className="flex flex-col gap-10 border-l border-paper/12 pl-1">
              {m.phase1.map((s, i) => (
                <Step key={i} n={`0${i + 1}`} t={s.t} d={s.d} />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-10 font-sans text-[11px] uppercase tracking-[0.2em] text-teal">{m.phase2Label}</p>
            <div className="flex flex-col gap-10 border-l border-paper/12 pl-1">
              {m.phase2.map((s, i) => (
                <Step key={i} n={`0${i + 5}`} t={s.t} d={s.d} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 grid gap-4 md:mt-20 md:grid-cols-3 md:gap-6">
          {[
            { src: "/images/osrelis-34.png", cap: m.imgC },
            { src: "/images/osrelis-32.png", cap: m.imgA },
            { src: "/images/osrelis-33.png", cap: m.imgB },
          ].map((im, i) => (
            <Reveal key={i} delay={i * 0.1} className="group relative overflow-hidden">
              <img src={im.src} alt={im.cap} className="h-[260px] w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105 md:h-[340px]" style={{ filter: "saturate(0.85) contrast(1.03)" }} />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(6,18,21,.65), transparent 55%)" }} />
              <span className="absolute bottom-4 left-4 font-sans text-[10px] uppercase tracking-[0.16em] text-white/85">{im.cap}</span>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid gap-px border-t border-paper/12 sm:grid-cols-2 lg:grid-cols-4">
          {m.metrics.map((metric, i) => (
            <Reveal key={i} delay={i * 0.08} className="border-b border-paper/12 py-8 pr-6 sm:border-r">
              <div className="font-serif text-[clamp(38px,4vw,60px)] leading-none text-paper" data-testid={`metric-${i}`}>
                <CountUp to={METRIC_VALUES[i].to} prefix={METRIC_VALUES[i].prefix} suffix={METRIC_VALUES[i].suffix} />
              </div>
              <div className="mt-4 font-sans text-[11px] uppercase tracking-[0.14em] text-sage">{metric.unit}</div>
              <p className="mt-2 font-sans text-[11px] leading-[1.6] text-paper/45">{metric.note}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
